import * as z from "zod";

export type MetadataValues = Record<string, unknown>;

export function unwrapMetadataSchema(schema: z.ZodType): z.ZodType {
  if (schema instanceof z.ZodOptional || schema instanceof z.ZodDefault) {
    return unwrapMetadataSchema(schema.unwrap() as z.ZodType);
  }
  return schema;
}

export function metadataReference(schema: z.ZodType): string | undefined {
  const reference = unwrapMetadataSchema(schema).meta()?.reference;
  return typeof reference === "string" ? reference : undefined;
}

export function metadataDefinitions(values: MetadataValues, name: string): MetadataValues {
  const entries = values[name];
  if (typeof entries !== "object" || entries === null || Array.isArray(entries)) return {};
  return entries as MetadataValues;
}

export function createMetadataValue(schema: z.ZodType, values: MetadataValues = {}): unknown {
  if (schema instanceof z.ZodDefault) return schema.parse(undefined);
  const inner = unwrapMetadataSchema(schema);
  const initial = inner.meta()?.initial;
  const reference = metadataReference(inner);
  if (reference) {
    const entries = metadataDefinitions(values, reference);
    if (typeof initial === "string" && Object.hasOwn(entries, initial) && entries[initial] !== undefined) return initial;
    return Object.keys(entries).find(key => entries[key] !== undefined) ?? "";
  }
  if (initial !== undefined) return structuredClone(initial);
  if (inner instanceof z.ZodString) return "";
  if (inner instanceof z.ZodNumber) return inner.minValue ?? 0;
  if (inner instanceof z.ZodBoolean) return false;
  if (inner instanceof z.ZodEnum) return inner.options[0];
  if (inner instanceof z.ZodLiteral) return [...inner.values][0];
  if (inner instanceof z.ZodArray) {
    const { min } = metadataArrayLimits(inner);
    return Array.from({ length: min }, () => createMetadataValue(inner.element as z.ZodType, values));
  }
  if (inner instanceof z.ZodObject) {
    const fields = Object.entries(inner.shape) as [string, z.ZodType][];
    const required = fields.filter(([, child]) => !child.isOptional());
    return Object.fromEntries(required.map(([key, child]) => [key, createMetadataValue(child, values)]));
  }
  throw new Error(`Unsupported metadata schema: ${inner.type}`);
}

export function metadataArrayLimits(schema: z.ZodArray) {
  const json = z.toJSONSchema(schema);
  return { min: json.minItems ?? 0, max: json.maxItems ?? Infinity };
}

export type MetadataReference = { definition: string; name: string; path: (string | number)[] };

export function metadataReferences(schema: z.ZodType, value: unknown, path: (string | number)[] = []): MetadataReference[] {
  if (value === undefined) return [];
  const inner = unwrapMetadataSchema(schema);
  const reference = metadataReference(inner);
  if (reference && typeof value === "string") return [{ definition: reference, name: value, path }];
  if (inner instanceof z.ZodArray && Array.isArray(value)) {
    return value.flatMap((item, index) => metadataReferences(inner.element as z.ZodType, item, [...path, index]));
  }
  if (inner instanceof z.ZodObject && typeof value === "object" && value !== null) {
    const record = value as MetadataValues;
    const shape: Record<string, z.ZodType> = inner.shape;
    return Object.entries(record).flatMap(([key, item]) => {
      const child = Object.hasOwn(shape, key) ? shape[key] : inner.def.catchall;
      if (!child) return [];
      return metadataReferences(child as z.ZodType, item, [...path, key]);
    });
  }
  return [];
}
